import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import AppContext from './context/AppContext';
import ErrorBoundary from './components/ErrorBoundary';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// --- EAGER IMPORTS ---
// LandingPage: first paint + prerender path — lazy would hurt LCP.
// PublicView: every profile visitor hits /:username — a loading flash is bad UX.
// Small shared UI components always present in the render tree.
import LandingPage from './LandingPage';
import { ToastContainer } from './components/ToastContainer';
import { ConfirmDialog } from './components/ConfirmDialog';
import { WelcomeModalWrapper } from './components/WelcomeModal';
import { ProtectedRoute, LandingRoute, AuthRoute, PreviewRoute, PublicProfileRoute } from './routes';
import { useProfileData } from './hooks/useProfileData';
import { useAutoSave } from './hooks/useAutoSave';

// --- LAZY IMPORTS ---
// Each route is split into its own chunk and only downloaded when navigated to.
// Suspense fallback is an inline spinner — no external imports needed.
// EditorView uses a named export — re-export as default so React.lazy can load it.
const EditorView     = lazy(() => import('./components/EditorView').then(m => ({ default: m.EditorView })));
// All page components use export default — no re-mapping needed.
const BlogPage       = lazy(() => import('./BlogPage'));
const BlogPostPage   = lazy(() => import('./BlogPostPage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
const PrivacyPolicy  = lazy(() => import('./pages/PrivacyPolicy'));
const SolutionsPage  = lazy(() => import('./pages/SolutionsPage'));
const TemplatesPage  = lazy(() => import('./pages/TemplatesPage'));
const ProfilesPage   = lazy(() => import('./pages/ProfilesPage'));

// --- FIREBASE IMPORTS ---
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebaseInstance';
import { getAppId } from './firebaseConfig';

/* -----------------------------------------------------------------------
FIREBASE CONFIGURATION & INIT
  -----------------------------------------------------------------------
*/
// 1. Initialize Firebase
// Note: Firebase services are now imported from firebaseInstance.js
// This allows components to share the same Firebase instances
const appId = getAppId();

// Note: Persistence is disabled to avoid multi-tab errors
// The app works fine with memory cache, which provides good performance
// without the complexity of IndexedDB persistence and multi-tab synchronization

/* -----------------------------------------------------------------------
   DATA & CONFIG
  -----------------------------------------------------------------------
*/
// PLATFORMS_CONFIG has been extracted to utils/platforms.js

/* -----------------------------------------------------------------------
   COMPONENTS
  -----------------------------------------------------------------------
*/
// ChatInterface has been extracted to components/ChatInterface.jsx


// AuthView has been extracted to components/AuthView.jsx

// TermsOfService, ComparisonPage, and PrivacyPolicy have been extracted to pages/

// MobilePreview, ProfileProgress, CharacterCounter, and WelcomeModal have been extracted to components/

// --- PUBLIC VIEW ---
// PublicView has been extracted to components/PublicView.jsx

// Route components have been extracted to routes/index.jsx

// --- MAIN ---
export default function App() {
  const [user, setUser] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false); // New flag to prevent overwriting
  const [isSaving, setIsSaving] = useState(false); // New saving state
  const [fieldSaveStatus, setFieldSaveStatus] = useState({}); // Track save status per field: { fieldName: 'saving' | 'saved' | null }
  const [toasts, setToasts] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {}, confirmText: 'Delete', cancelText: 'Cancel', confirmColor: 'red' });
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const location = useLocation(); // Define location early so it's available for useEffect

  // Toast notification system — useCallback so context consumers don't re-render
  // whenever the toast array changes (which would create a new function reference).
  const showToast = useCallback((message, type = 'success', duration = 3000) => {
    const id = Date.now();
    const toast = { id, message, type, onClose: () => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }};
    setToasts(prev => [...prev, toast]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  // Use profile data hook - MUST be called before any useEffect that uses profileData
  const {
    profileData,
    setProfileData,
    isLoadingFromFirestore,
    usernameError,
    setUsernameError,
    isCheckingUsername,
    setIsCheckingUsername,
    originalUsername,
    usernameCheckTimeoutRef,
    savingUsername,
    checkUsernameAvailability,
    saveUsername,
    lastSavedQnaRef,
    lastSaveTimeRef
  } = useProfileData(user, isLoaded, setIsLoaded, showToast);

  // 1. Initialize Auth - Listen for auth state changes
  // Defer auth initialization on landing page to improve initial load performance
  useEffect(() => {
    const isLandingPage = location.pathname === '/';
    let unsubscribe = null;
    let timeoutId = null;
    
    // On landing page, defer auth check to allow page to render first
    // This prevents the 1.5s Firebase auth iframe from blocking initial render
    if (isLandingPage) {
      // Defer auth initialization by 200ms on landing page
      // This allows the page to render immediately while auth loads in background
      timeoutId = setTimeout(() => {
        unsubscribe = onAuthStateChanged(auth, (user) => {
          setUser(user);
        });
      }, 200);
    } else {
      // On other routes, initialize auth immediately (needed for protected routes)
      unsubscribe = onAuthStateChanged(auth, (user) => {
        setUser(user);
      });
    }
    
    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [location.pathname]);

  // Memoize specific profileData fields to avoid unnecessary re-renders
  const qnaCompletedCount = (profileData?.qna || []).filter(
    q => q && q.answer && q.answer.trim().length > 0
  ).length;

  const profileDataFields = useMemo(() => ({
    hasSeenWelcome: profileData?.hasSeenWelcome,
    avatar: profileData?.avatar,
    username: profileData?.username,
    name: profileData?.name,
    bio: profileData?.bio,
    theme: profileData?.theme,
    linksLength: profileData?.links?.length || 0,
    qnaCompletedCount
  }), [
    profileData?.hasSeenWelcome,
    profileData?.avatar,
    profileData?.username,
    profileData?.name,
    profileData?.bio,
    profileData?.theme,
    profileData?.links?.length,
    qnaCompletedCount
  ]);

  // Check if user should see welcome modal
  // Only show on editor/preview routes, not on public profiles
  useEffect(() => {
    const currentPath = location.pathname;
    const isEditorOrPreview = currentPath === '/editor' || currentPath === '/preview';
    
    // Close modal if user navigates away from editor/preview routes
    if (!isEditorOrPreview && showWelcomeModal) {
      setShowWelcomeModal(false);
      return;
    }
    
    // Only show modal on editor/preview routes
    if (!isEditorOrPreview) {
      return;
    }
    
    if (user && isLoaded && profileDataFields) {
      // Check localStorage FIRST (immediate check), then Firestore (from profileData)
      const localStorageKey = user.uid ? `hasSeenWelcome_${user.uid}` : 'hasSeenWelcome_guest';
      const hasSeenWelcomeInStorage = localStorage.getItem(localStorageKey) === 'true';
      const hasSeenWelcomeInFirestore = profileDataFields.hasSeenWelcome === true;
      
      // If either source says they've seen it, don't show
      if (hasSeenWelcomeInStorage || hasSeenWelcomeInFirestore) {
        setShowWelcomeModal(false);
      return;
    }

      // Check if profile is mostly empty
      const isEmpty = !profileDataFields.username && (!profileDataFields.bio || profileDataFields.bio.trim().length === 0) && profileDataFields.linksLength === 0;

      // Profile is "complete" when all five welcome-modal steps are done.
      // Includes AI training (3 Q&A pairs) — the step that actually makes
      // the bio chat work. Old check ignored Q&A and dismissed the modal
      // for users whose AI was untrained, which masked the activation cliff.
      const hasUsername = !!profileDataFields.username;
      const hasIdentity = !!profileDataFields.name && !!profileDataFields.bio && profileDataFields.bio.trim().length > 0;
      const hasAITrained = profileDataFields.qnaCompletedCount >= 3;
      const hasLinks = profileDataFields.linksLength > 0;
      const hasPolish = !!profileDataFields.avatar && !!profileDataFields.theme;
      const isComplete = hasUsername && hasIdentity && hasAITrained && hasLinks && hasPolish;
      
      // Only show modal if profile is empty AND not complete
      if (isEmpty && !isComplete) {
        setTimeout(() => setShowWelcomeModal(true), 1000);
        } else {
        // If profile is complete or not empty, ensure modal is closed
        setShowWelcomeModal(false);
      }
    }
  }, [user, isLoaded, location.pathname, profileDataFields, showWelcomeModal]);

  // Use auto-save hook
  useAutoSave(
    user,
    isLoaded,
    isLoadingFromFirestore,
    profileData,
    originalUsername,
    lastSavedQnaRef,
    lastSaveTimeRef,
    setIsSaving,
    setFieldSaveStatus,
    showToast
  );

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  // Memoized context value — stable object reference as long as its deps don't change.
  // showToast and handleLogout are useCallback-wrapped so they're stable across renders.
  const appContextValue = useMemo(() => ({
    user,
    showToast,
    setConfirmDialog,
    onLogout: handleLogout,
    profileData,
    setProfileData,
    isSaving,
    fieldSaveStatus,
    originalUsername,
    checkUsernameAvailability,
    usernameError,
    setUsernameError,
    isCheckingUsername,
    setIsCheckingUsername,
    usernameCheckTimeoutRef,
    saveUsername,
    savingUsername,
    lastSavedQnaRef,
    lastSaveTimeRef,
  }), [
    user,
    showToast,
    setConfirmDialog,
    handleLogout,
    profileData,
    setProfileData,
    isSaving,
    fieldSaveStatus,
    originalUsername,
    checkUsernameAvailability,
    usernameError,
    setUsernameError,
    isCheckingUsername,
    setIsCheckingUsername,
    usernameCheckTimeoutRef,
    saveUsername,
    savingUsername,
    lastSavedQnaRef,
    lastSaveTimeRef,
  ]);

  return (
    <AppContext.Provider value={appContextValue}>
      <ToastContainer toasts={toasts} />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        confirmColor={confirmDialog.confirmColor}
      />
      <WelcomeModalWrapper
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        profileData={profileData}
        user={user}
        setProfileData={setProfileData}
      />
      <ErrorBoundary>
      {/* Suspense wraps Routes so lazy chunks can suspend without violating
          React Router v7's rule that only <Route>/<React.Fragment> may be
          direct children of <Routes>. */}
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-[#0F0F0F]">
          <div className="w-8 h-8 border-2 border-[#C1FF00] border-t-transparent rounded-full animate-spin" />
        </div>
      }>
      <Routes>

        {/* Landing Page — eager, first paint */}
        <Route path="/" element={<LandingRoute user={user} />} />

        {/* Auth */}
        <Route path="/auth" element={<AuthRoute user={user} />} />

        {/* Editor — lazy + second ErrorBoundary so editor crashes don't take down
            the app shell (ToastContainer, ConfirmDialog stay visible) */}
        <Route
          path="/editor"
          element={
            <ProtectedRoute user={user} isLoaded={isLoaded}>
              <ErrorBoundary>
                <EditorView />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Preview */}
        <Route
          path="/preview"
          element={
            <ProtectedRoute user={user} isLoaded={isLoaded}>
              <PreviewRoute profileData={profileData} user={user} />
            </ProtectedRoute>
          }
        />

        {/* Static pages — all lazy */}
        <Route path="/privacy"    element={<PrivacyPolicy />} />
        <Route path="/terms"      element={<TermsOfService />} />
        <Route path="/comparison" element={<ComparisonPage />} />
        <Route path="/solutions"  element={<SolutionsPage />} />
        <Route path="/templates"  element={<TemplatesPage />} />
        <Route path="/profiles"   element={<ProfilesPage />} />

        {/* Blog — lazy */}
        <Route path="/blog"       element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Reserved-word redirects */}
        <Route path="/login"     element={<Navigate to="/auth" replace />} />
        <Route path="/dashboard" element={<Navigate to="/auth" replace />} />
        <Route path="/settings"  element={<Navigate to="/auth" replace />} />
        <Route path="/account"   element={<Navigate to="/auth" replace />} />
        <Route path="/signup"    element={<Navigate to="/auth?mode=signup" replace />} />
        <Route path="/register"  element={<Navigate to="/auth?mode=signup" replace />} />

        {/* Public Profile — eager (core product, every profile visitor) */}
        <Route path="/:username" element={<PublicProfileRoute />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
      </Suspense>
      </ErrorBoundary>
    </AppContext.Provider>
  );
}